import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  InputBase,
  TextField,
} from "@mui/material";
import "./index.scss";

// Full Udyam registration number format ex: UDYAM-AP-03-0003870
export const UDYAM_REG_EX = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;

const PARTIAL_UDYAM_REG_EX = /^UDYAM-([A-Z]{0,2})-?(\d{0,2})-?(\d{0,7})$/;

const parseUdyamValue = (value) => {
  const match = (value || "").match(PARTIAL_UDYAM_REG_EX);
  if (!match) {
    return { stateCode: "", districtCode: "", uniqueNumber: "" };
  }
  return {
    stateCode: match[1] || "",
    districtCode: match[2] || "",
    uniqueNumber: match[3] || "",
  };
};

const UdyamNumberInput = ({
  name = "msmeNumber",
  label = "MSME NO",
  value = "",
  onChange,
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  isLegacy = false,
  legacyFieldName,
}) => {
  const [isEditingLegacy, setIsEditingLegacy] = useState(false);
  const showLegacyDisplay = isLegacy && !!value && !isEditingLegacy;

  const initial = parseUdyamValue(value);
  const [stateCode, setStateCode] = useState(initial.stateCode);
  const [districtCode, setDistrictCode] = useState(initial.districtCode);
  const [uniqueNumber, setUniqueNumber] = useState(initial.uniqueNumber);
  const [isFocused, setIsFocused] = useState(false);

  const stateRef = useRef(null);
  const districtRef = useRef(null);
  const uniqueRef = useRef(null);

  const hasValue = Boolean(stateCode || districtCode || uniqueNumber);
  const showBoxes = isFocused || hasValue;
  const labelFloated = showBoxes;

  const handleContainerFocus = () => setIsFocused(true);
  const handleContainerBlur = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (
        active !== stateRef.current &&
        active !== districtRef.current &&
        active !== uniqueRef.current
      ) {
        setIsFocused(false);
      }
    }, 0);
  };

  useEffect(() => {
    const parsed = parseUdyamValue(value);
    setStateCode(parsed.stateCode);
    setDistrictCode(parsed.districtCode);
    setUniqueNumber(parsed.uniqueNumber);
  }, [value]);

  const emitChange = (nextStateCode, nextDistrictCode, nextUniqueNumber) => {
    if (!onChange) {
      return;
    }
    const udyamNumber = `UDYAM-${nextStateCode}-${nextDistrictCode}-${nextUniqueNumber}`;
    onChange({
      target: {
        name,
        value: udyamNumber,
      },
    });
  };

  const startEditingLegacyValue = () => {
    setIsEditingLegacy(true);
    if (onChange) {
      onChange({
        target: { name: legacyFieldName || `${name}IsLegacy`, value: false },
      });
      onChange({ target: { name, value: "" } });
    }
  };

  const distributePastedValue = (pasted) => {
    const fullMatch = (pasted || "")
      .trim()
      .toUpperCase()
      .match(/^UDYAM-([A-Z]{2})-(\d{2})-(\d{7})$/);
    if (!fullMatch) {
      return false;
    }
    const [, pastedState, pastedDistrict, pastedUnique] = fullMatch;
    setStateCode(pastedState);
    setDistrictCode(pastedDistrict);
    setUniqueNumber(pastedUnique);
    emitChange(pastedState, pastedDistrict, pastedUnique);
    uniqueRef.current?.focus();
    return true;
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text");
    if (distributePastedValue(pasted)) {
      event.preventDefault();
    }
  };

  const handleStateCodeChange = (event) => {
    const nextValue = event.target.value
      .toUpperCase()
      .replace(/[^A-Z]/g, "")
      .slice(0, 2);
    setStateCode(nextValue);
    emitChange(nextValue, districtCode, uniqueNumber);
    if (nextValue.length === 2) {
      districtRef.current?.focus();
    }
  };

  const handleDistrictCodeChange = (event) => {
    const nextValue = event.target.value.replace(/[^0-9]/g, "").slice(0, 2);
    setDistrictCode(nextValue);
    emitChange(stateCode, nextValue, uniqueNumber);
    if (nextValue.length === 2) {
      uniqueRef.current?.focus();
    }
  };

  const handleUniqueNumberChange = (event) => {
    const nextValue = event.target.value.replace(/[^0-9]/g, "").slice(0, 7);
    setUniqueNumber(nextValue);
    emitChange(stateCode, districtCode, nextValue);
  };

  const focusPrevOnBackspace = (previousRef) => (event) => {
    if (event.key === "Backspace" && !event.target.value) {
      previousRef.current?.focus();
    }
  };

  if (showLegacyDisplay) {
    return (
      <FormControl
        error={error}
        disabled={disabled}
        required={required}
        className="udyam-number-input"
      >
        <div className="udyam-legacy-display">
          <TextField
            size="small"
            fullWidth
            label={label}
            required={required}
            error={error}
            value={value}
            disabled={disabled}
            onChange={(event) => {
              if (onChange) {
                onChange({ target: { name, value: event.target.value } });
              }
            }}
            inputProps={{
              "aria-label": "Existing MSME/Udyam number on record",
            }}
          />
          {!disabled && (
            <Button size="small" onClick={startEditingLegacyValue}>
              Change
            </Button>
          )}
        </div>
        <FormHelperText>
          Click 'CHANGE' to convert to new format
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      error={error}
      disabled={disabled}
      required={required}
      className="udyam-number-input"
    >
      <div
        className={[
          "udyam-number-input-boxes",
          error ? "has-error" : "",
          disabled ? "is-disabled" : "",
          showBoxes ? "is-expanded" : "is-collapsed",
        ]
          .filter(Boolean)
          .join(" ")}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
      >
        {label && (
          <span
            className={[
              "udyam-floating-label",
              labelFloated ? "floated" : "",
              labelFloated && isFocused && !error ? "is-focused" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {label}
            {required ? " *" : ""}
          </span>
        )}
        {showBoxes && (
          <>
            <span className="udyam-static-text">UDYAM</span>
            <span className="udyam-static-text">-</span>
          </>
        )}
        <InputBase
          inputRef={stateRef}
          disabled={disabled}
          error={error}
          value={stateCode}
          onChange={handleStateCodeChange}
          onPaste={handlePaste}
          className={`udyam-box udyam-box-state ${
            !showBoxes ? "udyam-box-collapsed" : ""
          }`}
          inputProps={{
            maxLength: 2,
            "aria-label": "State code, 2 letters",
          }}
        />
        {showBoxes && (
          <>
            <span className="udyam-static-text">-</span>
            <InputBase
              inputRef={districtRef}
              disabled={disabled}
              error={error}
              value={districtCode}
              onChange={handleDistrictCodeChange}
              onKeyDown={focusPrevOnBackspace(stateRef)}
              onPaste={handlePaste}
              className="udyam-box udyam-box-district"
              inputProps={{
                maxLength: 2,
                inputMode: "numeric",
                "aria-label": "District code, 2 digits",
              }}
            />
            <span className="udyam-static-text">-</span>
            <InputBase
              inputRef={uniqueRef}
              disabled={disabled}
              error={error}
              value={uniqueNumber}
              onChange={handleUniqueNumberChange}
              onKeyDown={focusPrevOnBackspace(districtRef)}
              onPaste={handlePaste}
              className="udyam-box udyam-box-unique"
              inputProps={{
                maxLength: 7,
                inputMode: "numeric",
                "aria-label": "Unique registration number, 7 digits",
              }}
            />
          </>
        )}
      </div>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default UdyamNumberInput;
